import { momentsOf, momentWords } from '@app/formats'

/** A node in the tree of elements, as far as this walk reads one. */
type Node = {
  type: string
  tagName?: string
  value?: string
  properties?: Record<string, unknown>
  children?: Node[]
}

/**
 * Where an instant is the text itself, so it is drawn as written: code, and a
 * heading, whose words the contents and the search read from the source.
 */
const AS_WRITTEN = new Set([
  'pre',
  'code',
  'time',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
])

/** A moment as an element: the instant kept, the words on this reader's clock. */
function momentElement(iso: string): Node {
  return {
    type: 'element',
    tagName: 'time',
    properties: { dateTime: iso },
    children: [{ type: 'text', value: momentWords(iso) }],
  }
}

/** A text's words with each moment in them drawn as a time; null where none is. */
function withMoments(value: string): Node[] | null {
  const out: Node[] = []
  let at = 0
  for (const found of momentsOf(value)) {
    const start = found.index ?? 0
    if (start > at) out.push({ type: 'text', value: value.slice(at, start) })
    out.push(momentElement(found[0]))
    at = start + found[0].length
  }
  if (at === 0) return null
  if (at < value.length) out.push({ type: 'text', value: value.slice(at) })
  return out
}

/**
 * Every moment in the words a body holds, written on the reader's own clock.
 *
 * A body stored once is read by several people, so it holds the UTC instant and
 * each reader is shown their own day and hour — the browser's zone is theirs.
 */
export function timeMoments(tree: Node) {
  const waiting: Node[] = [tree]
  for (let node = waiting.pop(); node; node = waiting.pop()) {
    const children = node.children
    if (!children || AS_WRITTEN.has(node.tagName ?? '')) continue
    let next: Node[] | null = null
    children.forEach((child, i) => {
      const drawn =
        child.type === 'text' ? withMoments(child.value ?? '') : null
      if (drawn) {
        next ??= children.slice(0, i)
        next.push(...drawn)
        return
      }
      if (child.type !== 'text') waiting.push(child)
      next?.push(child)
    })
    if (next) node.children = next
  }
}
